import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { type AcademicSnapshot, type FallbackTier, CHONBURI } from "@chonburi/shared";
import { useTheme } from "../hooks/useTheme";
import { formatDate } from "../lib/time";

interface FeedHealth {
  label: string;
  tier: FallbackTier | "loading";
  ageMinutes: number;
}

interface Props {
  feeds: FeedHealth[];
  onOpenCatalog: () => void;
  catalogCount: number;
  viewMode: "2D" | "3D" | "3DS";
  onCycleViewMode: () => void;
  onOpenManual: () => void;
  onOpenWhitepaper: () => void;
  onOpenSheets: () => void;
  onOpenBrief: () => void;
  onOpenDesk: () => void;
  sheetsConfigured: boolean;
  academic: AcademicSnapshot | null;
}


const TEMPO_COLOR: Record<AcademicSnapshot["tempo"], string> = {
  low: "var(--text-3)",
  normal: "var(--data)",
  high: "var(--warn)",
  peak: "var(--bad)",
};

export function TopBar({ feeds, onOpenCatalog, catalogCount, viewMode, onCycleViewMode, onOpenManual, onOpenWhitepaper, onOpenSheets, onOpenBrief, onOpenDesk, sheetsConfigured, academic }: Props) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // On a phone the world strip shares the masthead's grid cell, so it has to
  // stop where the controls begin. That distance is measured rather than
  // hard-coded: a fixed reserve silently overlaps as soon as a button is
  // added, and the strip clips its overflow with no scrollbar to show for it.
  const rightRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = rightRef.current;
    const shell = el?.closest(".shell") as HTMLElement | null;
    if (!el || !shell) return;
    const apply = () => {
      const reserve = shell.getBoundingClientRect().right - el.getBoundingClientRect().left;
      if (reserve > 0) shell.style.setProperty("--topbar-right-w", `${Math.ceil(reserve)}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    ro.observe(shell);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);

  const { theme, toggle } = useTheme();

  // The feed state, in the order a reader needs it: what is live, what is
  // not, and by name.
  //
  // This used to be ten identical chips — 431 px of the 1,440 px bar — plus a
  // separate count. Ten pixels of status per feed is not information a reader
  // can act on; the same fact, as a count and a list of the feeds that are
  // not live, is. The per-source detail lives in the Sources catalog, which
  // this button opens.
  const live = feeds.filter((f) => f.tier === "live");
  const down = feeds.filter((f) => f.tier === "unavailable");
  const stale = feeds.filter((f) => f.tier === "cache" || f.tier === "database" || f.tier === "reference");
  const loading = feeds.filter((f) => f.tier === "loading");
  const problems = [
    ...down.map((f) => `${f.label} down`),
    ...stale.map((f) => `${f.label} cached/reference`),
    ...feeds.filter(f => f.tier === "scenario").map(f => `${f.label} example`),
  ];
  const shown = problems.slice(0, 2);
  const rest = problems.length - shown.length;
  const dotState = down.length > 0 ? "unavailable" : stale.length > 0 ? "degraded" : loading.length > 0 ? "loading" : "live";
  const waiting = live.length === 0 && loading.length > 0;
  const detail = shown.length > 0
    ? `${shown.join(" · ")}${rest > 0 ? ` · +${rest} more` : ""}`
    : "";
  const feedTitle = feeds
    .map((f) => `${f.label}: ${f.tier}${f.tier === "loading" ? "" : ` · ${f.ageMinutes}m ago`}`)
    .join("\n");

  return (
    <header className="topbar">
      {/* Sponsor + identity bar */}
      <div className="brand">
        {/* Municipal seal — principal identity mark */}
        <a className="sponsor sponsor-seal" href="https://www.chonburi.go.th" target="_blank"
           rel="noreferrer" aria-label="เทศบาลเมืองชลบุรี — Chonburi Town Municipality">
          <img src="/logos/chonburi-seal.jpeg" alt="Chonburi seal" />
        </a>
        {/* Partners */}
        <a className="sponsor" href="https://www.depa.or.th" target="_blank"
           rel="noreferrer" aria-label="depa — Digital Economy Promotion Agency">
          <img src="/logos/depa.jpg" alt="depa" />
        </a>
        <a className="sponsor" href="https://www.smartcitythailand.or.th" target="_blank"
           rel="noreferrer" aria-label="Smart City Thailand">
          <img src="/logos/smart-city-thailand.jpg" alt="Smart City Thailand" />
        </a>
        <a className="sponsor sponsor-axiom" href="https://axiom.nonarkara.org" target="_blank"
           rel="noreferrer" aria-label="Axiom — Innovation as a Service">
          <img src="/logos/axiom.png" alt="Axiom" />
        </a>
        <svg className="brand-wave" width="42" height="22" viewBox="0 0 42 22" aria-hidden>
          <path d="M0 11 Q 5 4, 10 11 T 20 11 T 30 11 T 42 11"
                fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.95"/>
          <path d="M0 17 Q 5 10, 10 17 T 20 17 T 30 17 T 42 17"
                fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.5"/>
        </svg>
        <div className="brand-stack">
          <strong><span className="brand-name-full">{CHONBURI.name.en}</span><span className="brand-name-compact">Chonburi IOC</span></strong>
          <span className="brand-sub mono">
            CTM-01 · Eastern Seaboard
          </span>
        </div>
      </div>

      <div className="topbar-right" ref={rightRef}>
        <button
          onClick={onOpenCatalog}
          className={`mono live-count feed-state-${dotState}`}
          aria-live="polite"
          aria-label={
            waiting
              ? `Data feeds are still loading (${loading.length} of ${feeds.length})`
              : `${live.length} of ${feeds.length} data feeds live${detail ? ` — ${detail}` : ""}`
          }
          title={`${feedTitle}\n\nOpens the source catalog`}
        >
          <span className={`dot ${dotState}`} style={{ marginRight: 5 }} />
          {waiting ? "loading" : `${live.length}/${feeds.length} live`}
          {detail && <span className="live-count-detail">{" · "}{detail}</span>}
        </button>
        <button
          onClick={onOpenSheets}
          className={`mono sheets-btn ${sheetsConfigured ? "sheets-btn-live" : ""}`}
          aria-label={sheetsConfigured ? "Open Google Sheets live feed" : "Set up Google Sheets integration"}
          title={sheetsConfigured ? "Open the live-data Google Sheet" : "Connect a Google Sheet to this dashboard"}
        >
          Sheets
        </button>
        <button
          onClick={onOpenDesk}
          className="mono desk-trigger"
          aria-label="เปิดโต๊ะนายกเมือง — Open mayor's desk"
          title="Mayor's desk — the morning read: conditions, and the news in other languages, translated"
        >
          โต๊ะ
        </button>
        <button
          onClick={onOpenBrief}
          className="mono brief-trigger"
          aria-label="เปิดสรุปสำหรับนายกเมือง — Open mayor brief (TH/EN)"
          title="Mayor brief — four conditions, Thai and English, ready to forward"
        >
          Brief
        </button>
        <button onClick={onOpenCatalog} className="mono" aria-label="Open source catalog">
          <span className="catalog-label">Sources · {catalogCount}</span><span className="catalog-icon" aria-hidden="true">≡</span>
        </button>
        <button
          onClick={onOpenWhitepaper}
          className="mono whitepaper-trigger"
          aria-label="Open whitepaper — platform overview and usage manual (TH/EN)"
          title="Whitepaper — platform overview · Thai + English"
        >
          Guide
        </button>
        <button
          onClick={onOpenManual}
          className="mono manual-trigger"
          aria-label="Open user manual"
          title="Manual — every button, color, acronym"
        >
          ?
        </button>
        <button
          onClick={onCycleViewMode}
          className={`mono dim-toggle vm-${viewMode}`}
          aria-label={`View mode: ${viewMode}. Click to cycle 2D → 3D → 3D substructure`}
          title={
            viewMode === "2D"
              ? "Tap → 3D · extrude buildings"
              : viewMode === "3D"
                ? "Tap → 3DS · transparent superstructure, show underground"
                : "Tap → 2D · top-down"
          }
        >
          {viewMode}
        </button>
        <button
          onClick={toggle}
          className="theme-toggle"
          aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          title={`${theme === "dark" ? "Light" : "Dark"} theme`}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            {theme === "dark" ? <><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5" /></> : <path d="M20 15A8 8 0 0 1 9 4a8 8 0 1 0 11 11Z" />}
          </svg>
        </button>
      </div>

      <div className="clock">
        {academic?.current && (
          <span
            className="mono academic-chip"
            style={{ color: TEMPO_COLOR[academic.tempo] }}
            title={`${academic.current.label} · ${academic.current.describe}${
              academic.next && academic.daysToNext != null
                ? ` · next: ${academic.next.label} in ${academic.daysToNext}d`
                : ""
            }`}
          >
            {academic.current.label.toUpperCase()}
          </span>
        )}
        <span className="clock-date">{formatDate(now)}</span>
        <span className="clock-time">
          {now.toLocaleTimeString("en-GB", { hour12: false })}
        </span>
      </div>
    </header>
  );
}
